import { useState, useEffect } from 'react';
import {
  setItem,
  removeItemsStartsWith,
  getItem,
} from 'app/services/storage.service';
import { useNotification } from 'app/shared/hooks/useNotification';
import { useCustomEvents } from 'app/shared/hooks/useCustomEvents';
import {
  LabelEnum,
  MessageTypeEnum,
  CustomEventsNameEnum,
  InstallmentsEnum,
} from 'app/shared/enums';

import { useToggle } from 'app/shared/hooks/useToggle';
import { OfferInterface } from 'app/shared/interfaces/Offer.interface';
import { HiProConsentWithTextDto } from 'app/shared/interfaces/HiProApplicationResponse.interface';
import { HiProPaymentService } from 'app/services/hiProPayment.services';
import { HiProDecisionStatus } from 'app/shared/interfaces/HiProDecisionStatus.interface';

import { InstallmentOffer } from './MubiInstallmentsRadioOption.interfaces';
import { TagManagerService } from 'app/services/tagManager.service';

export function useMubiInstallments(offer: OfferInterface | undefined) {
  const notify = useNotification();
  const [
    hiProInstalmentContractConfirmed,
    setHiProInstalmentContractConfirmed,
  ] = useState(false);

  const [isRejected, setIsRejected] = useState(false);

  const [hiProToken, setHiProToken] = useState<string | undefined>(undefined);
  const [hiProConsents, setHiProConsents] = useState<HiProConsentWithTextDto[]>(
    []
  );
  const [installmentOffer, setInstallmentOffer] = useState<
    InstallmentOffer | undefined
  >();
  const [errorOnModal, setErrorOnModal] = useState<
    { title: string; message: string; btnLabel: string } | undefined
  >(undefined);
  const [
    showAllerMubiInstallmentsApplicationNotFilled,
    setShowAlertMubiInstallmentsApplicationNotFilled,
  ] = useState(false);
  const { subscribeEvent, unsubscribeEvent, triggerEvent } = useCustomEvents();
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mubiInstallmentsRrsoExpanded, toggle] = useToggle(false);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    if (offer) {
      const { token } = getItem(`hiPro_token_offer_${offer.id}`);

      if (token) {
        const { finalDecision } = getItem(`hiPro_final_decision_${token}`);
        const { verificationDecision } = getItem(
          `hiPro_verification_decision_${token}`
        );

        if (verificationDecision) {
          setIsRejected(true);
          return;
        }
        const { step } = getItem(`hiPro_step_${token}`);
        step && setActiveStep(step);

        const fetchDecision = async () => {
          const { data } = await HiProPaymentService.getHiProCreditDecision({
            token,
          });
          if (data.value === HiProDecisionStatus.Rejected) {
            setIsRejected(true);
          } else {
            setHiProInstalmentContractConfirmed(data.contractConfirmed);
          }
        };
        if (step > 3 && finalDecision) {
          fetchDecision().catch(() => {
            notify(MessageTypeEnum.Danger, LabelEnum.ServerError);
          });
        }

        setHiProToken(token);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    triggerEvent(CustomEventsNameEnum.MubiInstallmentsContractConfirmed, {
      contractConfirmed: hiProInstalmentContractConfirmed,
    });
  }, [hiProInstalmentContractConfirmed, triggerEvent]);

  useEffect(() => {
    subscribeEvent(
      CustomEventsNameEnum.MubiInstallmentsApplicationNotFilledError,
      (e: CustomEvent) => {
        setShowAlertMubiInstallmentsApplicationNotFilled(e.detail.showError);
      }
    );
    return () => {
      unsubscribeEvent(
        CustomEventsNameEnum.MubiInstallmentsApplicationNotFilledError,
        () => {}
      );
    };
  }, [unsubscribeEvent, subscribeEvent]);

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = (stepName: string | undefined) => {
    setIsModalOpen(false);

    if (stepName) {
      mubiInstallmentsDataLayerEvent(
        InstallmentsEnum.EventNameModalExit,
        stepName
      );
    }
  };

  const mubiInstallmentsDataLayerEvent = (event: string, stepName: string) => {
    const dataLayer = {
      event,
      modal_name: InstallmentsEnum.EventNameHipro,
      step_number: `${activeStep + 1}`,
      step_name: stepName,
    };

    TagManagerService.addDataLayer(dataLayer);
  };

  const goToSelectedStep = (step: number) => {
    setActiveStep(step);
    if (step >= 2) {
      setItem(`hiPro_step_${hiProToken}`, { step });
    }
  };

  const clearTokenState = () => {
    removeItemsStartsWith('hiPro');
    setActiveStep(0);
    setHiProToken(undefined);
  };

  return {
    error,
    setError,
    openModal,
    closeModal,
    goToSelectedStep,
    mubiInstallmentsRrsoExpanded,
    toggle,
    activeStep,
    isModalOpen,
    showAllerMubiInstallmentsApplicationNotFilled,
    errorOnModal,
    setErrorOnModal,
    isRejected,
    hiProConsents,
    setHiProConsents,
    installmentOffer,
    setInstallmentOffer,
    hiProToken,
    setHiProToken,
    setHiProInstalmentContractConfirmed,
    setIsRejected,
    setShowAlertMubiInstallmentsApplicationNotFilled,
    hiProInstalmentContractConfirmed,
    clearTokenState,
    mubiInstallmentsDataLayerEvent,
  };
}
